"use strict";

import $ from "jquery";

import {Alert} from "@/components/alert/Alert.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";

/**
 * Returns a unique HTML ID.
 * Call and store this in the component constructor.
 */
let uniqueHtmlIdCounter = 0;

export function uniqueHtmlId(prefix) {
  uniqueHtmlIdCounter++;
  prefix = prefix || "react-id";
  return prefix + "--" + uniqueHtmlIdCounter + "--";
}

/**
 * Creates an object with get() and set() methods used
 * to link form state from a child component to a parent.
 *
 * @param formComponent A reference to the React component that form state is
 *     saved on.
 * @param stateKey The string key in the component state where form data is stored.
 * @param formErrors Optional prop containing a dict with form errors.
 */
export function prepareFormLink(formComponent, stateKey, formErrors) {
  const get = (fieldKey) => formComponent.state[stateKey][fieldKey];

  const set = (fieldKey, nextHandler, e) => {
    if (!e) {
      e = nextHandler;
      nextHandler = null;
    }

    let newValue;
    switch (e.target.type) {
      case "checkbox":
        newValue = e.target.checked;
        break;

      case "file":
        newValue = e.target.files.length > 0 ? e.target.files[0] : null;
        break;

      default:
        newValue = e.target.value;
        break;
    }

    const oldState = formComponent.state[stateKey];
    const newState = Object.assign({}, oldState, {[fieldKey]: newValue});
    formComponent.setState({[stateKey]: newState});

    if (nextHandler) {
      nextHandler(e, newValue);
    }
  };

  const errors = (fieldKey) => (formErrors ? formErrors[fieldKey] : null);

  const forField = (fieldKey) => ({
    get: get.bind(null, fieldKey),
    set: set.bind(null, fieldKey),
    errors: errors.bind(null, fieldKey),
  });

  return {
    get: get,
    set: set,
    errors: errors,
    forField: forField,
  };
}

/**
 * Assuming standard prop names fieldName, formLink, formErrors, value, onChange, returns
 * an object containing those keys after deciding whether to use
 * the formLink or standalone values.
 */
export function unpackFormLinkOrProps(props) {
  const fieldName = props.fieldName;
  const formLink = props.formLink ? props.formLink.forField(fieldName) : null;

  let value = formLink ? formLink.get() : props.value;
  if (value === undefined || value === null) {
    value = "";
  }

  return {
    fieldName: fieldName,
    formLink: formLink,
    value: value,
    onChange: formLink ? formLink.set.bind(null, props.onChange) : props.onChange,
    errors: (formLink && formLink.errors()) || (props.formErrors && props.formErrors[fieldName]) || null,
  };
}

/**
 * Substitute for React's SyntheticEvent for custom form components.
 */
export class CustomEvent {
  constructor({type = "custom", target = null, value = undefined}) {
    this.bubbles = false;
    this.cancelable = false;
    this.currentTarget = target;
    this.defaultPrevented = true;
    this.eventPhase = 0;
    this.nativeEvent = null;
    this.preventDefault = () => null;
    this.isDefaultPrevented = () => true;
    this.stopPropagation = () => null;
    this.isPropagationStopped = () => true;
    this.target = target;
    this.timeStamp = Date.now();
    this.type = type;

    if (value !== undefined) {
      this.target.value = value;
    }
  }
}

/**
 * Checks if a value contains HTML code.
 */
function valueContainsHTML(v) {
  return v.indexOf("</a>") !== -1 || v.indexOf("</strong>") !== -1 || v.indexOf("</em>") !== -1;
}

/**
 * Create a bootstrap global form errors component.
 * Accepts `errors` prop.
 */
export function FormErrors(props) {
  if (!props.errors || $.isEmptyObject(props.errors)) {
    return null;
  }

  const errs = props.errors.map((v) => {
    if (valueContainsHTML(v)) {
      return <li key={v} dangerouslySetInnerHTML={{__html: v}} />;
    } else {
      return <li key={v}>{v}</li>;
    }
  });

  return (
    <Alert variant={"error"}>
      <ul className="list-unstyled list-spacer-1 mb-0">{errs}</ul>
    </Alert>
  );
}

/**
 * Error text beneath form element. Accepts `errors` prop.
 */
export function FieldErrors(props) {
  if (!props.errors || $.isEmptyObject(props.errors)) {
    return null;
  }
  const errors = props.errors.join ? props.errors.join(" ") : props.errors;
  const body = valueContainsHTML(errors) ? <div dangerouslySetInnerHTML={{__html: errors}} /> : errors;
  return props.alert ? (
    <Alert variant="error" className="mt-3 text-break overflow-auto">
      {body}
    </Alert>
  ) : (
    <div className="pulse-color-red-500 pulseTextBodySmallBold mt-1 text-break overflow-auto">{body}</div>
  );
}

/**
 * Error text beneath form element. Accepts `helpText` prop.
 */
export function FieldHelpText(props) {
  if (!props.helpText) {
    return null;
  }

  return <FormSubtext subText={props.helpText} />;
}

/**
 * Combined HTML label & tooltip if relevant.
 * Accepts `baseCSSClass`, `fieldId`, `isRequired`, labelText`, `titleText` and `wizard` props.
 */
export function Label(props) {
  const baseCSSClass = props.baseCSSClass || "form-label";

  if (!props.labelText && baseCSSClass.indexOf("custom-control") === -1) {
    return null;
  }

  return (
    <FormLabel helpText={props.titleText} forId={props.fieldId} required={props.isRequired}>
      {props.labelText}
    </FormLabel>
  );
}
